/*
    0.98.8: when the fog's layer (warShaders.ts `FX_FRAGMENT`: the fog, the gas's rolling wall and the next zone's
    ring, drawn small and laid over the frame) comes out empty, every pixel of it nothing, it's neither drawn nor laid
    over the frame: laid over it, an empty layer changed nothing. Worked out here as the shader works it out, from
    the same uniforms, with a unit's margin.

    In the shader, at a point `d` game units past the zone's edge (inside it, `d` is negative): the wall's front is
    `edge = d + (billow - 0.5) * 12` with the billows between 0 and 1, so it reaches 6 units in from the edge at most;
    the wall is `smoothstep(-2, 10, edge)`, nothing for `edge` -2 and less; the wisps ahead of it
    `smoothstep(-7, 0, edge)`, nothing for -7 and less. Nothing of the gas, then, 13 units or more inside the zone.
    The ring: `px` the screen's pixels from it, its line `1 - smoothstep(k, 2.2 k, px)` (`k` the line's width, at
    least a pixel: `max(1, uPxSize * 0.6)`), its glow `1 - smoothstep(1.5, 9, px)`: nothing 9 pixels off and more,
    or 2.2 widths when they're wider. The fog has no such edge: with any fog, the layer is drawn.
*/

/** the fog layer's uniforms that say whether it's empty (warLook.ts sets them each frame) */
export interface FxLayerState {
    readonly fog: number
    readonly gasOn: number
    readonly gasX: number
    readonly gasY: number
    readonly gasRadius: number
    readonly nextOn: number
    readonly nextX: number
    readonly nextY: number
    readonly nextRadius: number
    /** the screen's pixels a pixel of the layer */
    readonly pxSize: number
}

/** how far inside the zone the gas's wall and its wisps reach at most (game units), and the margin kept past it */
export const GAS_REACH = 13;
/** the ring's glow's reach (the screen's pixels), and its line's in its widths */
export const RING_GLOW_PX = 9;
export const RING_LINE_WIDTHS = 2.2;
/**
 * the margin kept past each reach (game units): the card's own rounding of the world's units (a few thousandths of
 * one) many times over, and some for a card that works the layer out in less than full precision
 */
export const FX_MARGIN = 2;

/**
 * whether every pixel of the fog's layer comes out empty over the screen from (`x0`, `y0`) to (`x1`, `y1`) (game
 * units), `unitsPerPx` game units a pixel of the screen
 */
export function fxLayerClear(state: FxLayerState, x0: number, y0: number, x1: number, y1: number, unitsPerPx: number): boolean {
    if (!(state.fog <= 0)) return false;
    /** the screen's corner furthest from (x, y) */
    const furthest = (x: number, y: number): number => Math.hypot(Math.max(Math.abs(x - x0), Math.abs(x - x1)), Math.max(Math.abs(y - y0), Math.abs(y - y1)));
    /** the screen's point nearest (x, y) (0 on it) */
    const nearest = (x: number, y: number): number => Math.hypot(Math.max(0, x0 - x, x - x1), Math.max(0, y0 - y, y - y1));
    if (state.gasOn > 0.5 && !(furthest(state.gasX, state.gasY) <= state.gasRadius - GAS_REACH - FX_MARGIN)) return false;
    if (state.nextOn > 0.5) {
        const width = Math.max(1, state.pxSize * 0.6);
        const reach = Math.max(RING_GLOW_PX, RING_LINE_WIDTHS * width) * unitsPerPx + FX_MARGIN;
        const inside = furthest(state.nextX, state.nextY) <= state.nextRadius - reach;
        const outside = nearest(state.nextX, state.nextY) >= state.nextRadius + reach;
        if (!inside && !outside) return false;
    }
    return true;
}
